import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Product,
	Severity,
	Team,
} from '@atlassian/growth-commons-error-analytics';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { useHasBillingInformation } from '../../useHasBillingInformation';

import { useReverseTrialLozengeExperimentTrialDetails } from './useReverseTrialLozengeExperimentTrialDetails';

type UseReverseTrialLozengeEligibleProps = {
	experimentName: string;
	isGateEnabled: boolean;
};

export const useReverseTrialLozengeEligible = ({
	experimentName,
	isGateEnabled,
}: UseReverseTrialLozengeEligibleProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isLicensed, isLoggedIn } = useSessionData();

	const skip = !isLicensed || !isLoggedIn || !isGateEnabled;

	const {
		billingSourceSystem,
		daysLeft,
		error: isUseReverseTrialLozengeTrialDetailsError,
		loading: isUseReverseTrialLozengeTrialDetailsLoading,
	} = useReverseTrialLozengeExperimentTrialDetails(skip);

	const isCCP =
		!isUseReverseTrialLozengeTrialDetailsLoading &&
		!isUseReverseTrialLozengeTrialDetailsError &&
		billingSourceSystem === 'CCP';

	const {
		hasBillingInformation,
		loading: isHasBillingInformationLoading,
		error: isHasBillingInformationError,
	} = useHasBillingInformation(skip);

	if (isUseReverseTrialLozengeTrialDetailsError || isHasBillingInformationError) {
		const errorSource =
			(isUseReverseTrialLozengeTrialDetailsError && 'useReverseTrialLozengeTrialDetails') ||
			(isHasBillingInformationError && 'useHasBillingInformation');

		const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
			product: Product.CONFLUENCE,
			team: Team.GROWTH_LUNA,
			experimentMeta: {
				experimentKey: experimentName,
			},
			errorType: ErrorType.GRAPHQL,
			errorMessage: (isUseReverseTrialLozengeTrialDetailsError?.message ||
				isHasBillingInformationError?.message)!,
			source: `${errorSource} hook`,
			severity: Severity.HIGH,
		});
		createAnalyticsEvent(eventPayload).fire();

		return {
			isEligible: false,
			loading: false,
			error:
				isUseReverseTrialLozengeTrialDetailsError?.message || isHasBillingInformationError?.message,
		};
	}

	const isEligibleBilling = isCCP && !hasBillingInformation;
	const isInTrial = daysLeft !== null && daysLeft > 0;

	const isEligible = isLicensed && isLoggedIn && isInTrial && isGateEnabled && isEligibleBilling;

	return {
		isEligible,
		loading: isUseReverseTrialLozengeTrialDetailsLoading || isHasBillingInformationLoading,
		error: null,
	};
};
